import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import axiosInstance from "../axiosInstance";
import Cookies from "js-cookie";
import Swal from "sweetalert2";

export interface ApiCallConfig {
  method: "get" | "post" | "put" | "delete" | "patch";
  url: string;
  data?: any;
  params?: Record<string, any>;
  headers?: any;
}

async function makeApiCall<T>(config: ApiCallConfig): Promise<T> {
  // Retrieve the access token from the cookie right before the API call
  const accessToken = Cookies.get("access-token");
  let headers = accessToken ? { Authorization: `${accessToken}` } : {};
  if (config.headers) {
    headers = { ...headers, ...config.headers };
  }
  try {
    const axiosConfig: AxiosRequestConfig = {
      method: config.method,
      url: config.url,
      data: config.data,
      params: config.params,
      headers,
    };

    const response: AxiosResponse<T> = await axiosInstance(axiosConfig);
    return response.data;
  } catch (error: any) {
    if (error.message === "Token Expired") {
      Swal.fire({
        title: "Session Expired",
        text: "Re-login to continue",
        icon: "warning",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "Ok",
      }).then(async (result) => {
        if (result.isConfirmed) {
          window.location.href = "/";
        }
      });
      await new Promise((resolve) => setTimeout(resolve, 10000));
      window.location.href = "/";
    }
    return error;
  }
}

// API call function without any authorization token
export async function makeUnauthenticatedApiCall<T>(
  config: ApiCallConfig
): Promise<T> {
  try {
    // Create a new axios instance without interceptors to avoid token injection
    const unauthenticatedAxios = axios.create({
      baseURL: process.env.REACT_APP_API_LOGIN_URL,
      withCredentials: false,
    });

    const headers: any = {};

    // Set Content-Type only for non-FormData requests
    if (!(config.data instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }
    // Explicitly ensure no Authorization header
    delete headers.Authorization;

    const axiosConfig: AxiosRequestConfig = {
      method: config.method,
      url: config.url,
      data: config.data,
      params: config.params,
      headers,
    };

    const response: AxiosResponse<T> = await unauthenticatedAxios(axiosConfig);
    return response.data;
  } catch (error: any) {
    console.log("Error intercepted in makeUnauthenticatedApiCall:", error);
    // Return error response data if available, otherwise return the error itself
    if (error?.response?.data) {
      return error.response.data as T;
    }
    return error;
  }
}

export default makeApiCall;
